import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  sectionMargins,
  standardColours,
  brandColours,
  siteThemeColours,
  visuallyHidden,
} from '../styles';
import { Container, Heading, Button } from './ui';
import ImageCard from './ImageCard';
import leftLeaf from '../images/site-themes/mothers-day/left-leaf.png';
import rightLeaf from '../images/site-themes/mothers-day/right-leaf.png';
import winterBush from '../images/site-themes/christmas/winter-bush.png';

const StyledImageCards = styled.section`
  ${sectionMargins()};
  overflow-x: clip;
`;

const StyledContainer = styled(Container)`
  position: relative;
`;

const StyledLeaf = styled.img`
  &:first-of-type {
    position: absolute;

    ${maxBreakpointQuery.smedium`
      right: 20px;
      bottom: -60px;
    `}

    ${minBreakpointQuery.smedium`
      bottom: -40px;
      left: 20px;
    `}

    ${minBreakpointQuery.xxlarge`
      bottom: 35%;
      left: -80px;
    `}

    ${minBreakpointQuery.xxxlarge`
      left: -100px;
    `}

    ${minBreakpointQuery.xxxxlarge`
      left: -120px;
    `}
  }

  &:last-of-type {
    ${maxBreakpointQuery.smedium`
      ${visuallyHidden()};
    `}

    ${minBreakpointQuery.smedium`
      position: absolute;
      top: -40px;
      right: 20px;
    `}
  }
`;

const StyledHeader = styled.header`
  margin-bottom: 30px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 35px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 40px;
  `}

  ${({ siteTheme }) => {
    if (siteTheme) {
      return css`
        color: ${siteTheme === 'valentines'
          ? siteThemeColours.valentines.tertiary
          : siteTheme === 'mothersday'
            ? siteThemeColours.mothersDay.primary
            : siteTheme === 'summer'
              ? standardColours.black
              : siteTheme === 'christmas'
                ? siteThemeColours.christmas.tertiary
                : siteTheme === 'blackfriday'
                  ? standardColours.black
                  : 'inherit'};
      `;
    }
  }}
`;

const StyledText = styled.p`
  margin: 15px auto 0;
  max-width: 460px;

  ${({ siteTheme }) => {
    if (siteTheme === 'mothersday') {
      return css`
        color: ${standardColours.black};
      `;
    }
  }}
`;

const StyledItems = styled.div`
  display: grid;
  gap: 10px;

  ${minBreakpointQuery.tiny`
    gap: 15px;
  `}

  ${minBreakpointQuery.small`
    gap: 20px;
  `}

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${minBreakpointQuery.medium`
    gap: 30px;
  `}

  ${minBreakpointQuery.large`
    gap: 35px;
  `}
`;

const StyledItem = styled(ImageCard)`
  img {
    ${maxBreakpointQuery.smedium`
      height: 260px;
    `}

    ${maxBreakpointQuery.tsmall`
      height: 220px;
    `}
  }
`;

const StyledFooter = styled.footer`
  margin-top: 20px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const StyledButton = styled(Button)`
  ${({ siteTheme }) => {
    if (siteTheme) {
      return css`
        color: ${siteTheme === 'blackfriday'
          ? brandColours.tertiary
          : standardColours.white};
        background-color: ${siteTheme === 'christmas'
          ? siteThemeColours.christmas.primary
          : siteTheme === 'blackfriday'
            ? standardColours.black
            : brandColours.tertiary};
      `;
    }
  }}
`;

const StyledWinterBush = styled.img`
  &:first-of-type {
    position: absolute;
    right: -140px;
    bottom: -160px;
    width: 240px;

    ${minBreakpointQuery.tsmall`
      right: -180px;
      bottom: -180px;
      width: 280px;
    `}

    ${minBreakpointQuery.smedium`
      right: -220px;
      bottom: -200px;
      width: 320px;
    `}

    ${minBreakpointQuery.mlarge`
      right: -260px;
      bottom: -220px;
      width: 360px;
    `}

    ${minBreakpointQuery.xlarge`
      top: -100px;
      right: -360px;
      bottom: auto;
      width: 400px;
    `}

    ${minBreakpointQuery.xxlarge`
      right: -400px;
    `}
  }

  &:last-of-type {
    ${maxBreakpointQuery.xlarge`
      ${visuallyHidden()};
    `}

    ${minBreakpointQuery.xlarge`
      position: absolute;
      bottom: -180px;
      left: -240px;
      width: 400px;
    `}

    ${minBreakpointQuery.xxlarge`
      left: -280px;
    `}
  }
`;

const ImageCards = ({ heading, text, items, link, alt }) => {
  const {
    spaseekers: {
      promotion: { theme: siteTheme },
    },
  } = useStaticQuery(graphql`
    query ImageCardsQuery {
      spaseekers {
        promotion {
          theme
        }
      }
    }
  `);

  return (
    items &&
    items.length && (
      <StyledImageCards>
        <StyledContainer>
          {siteTheme === 'mothersday' && (
            <>
              <StyledLeaf src={leftLeaf} alt="Leaf" loading="lazy" />
              <StyledLeaf src={rightLeaf} alt="Leaf" loading="lazy" />
            </>
          )}
          {heading && (
            <StyledHeader siteTheme={siteTheme}>
              <Heading>{heading}</Heading>
              {text && <StyledText siteTheme={siteTheme}>{text}</StyledText>}
            </StyledHeader>
          )}
          <StyledItems>
            {items.map(({ label, heading, image, text, url }, i) => (
              <StyledItem
                key={`image-card-${i}`}
                label={label}
                heading={heading}
                image={image}
                text={text}
                url={url}
                alt={alt}
              />
            ))}
          </StyledItems>
          {link && (
            <StyledFooter>
              <StyledButton to={link.url} arrow={true} siteTheme={siteTheme}>
                {link.text}
              </StyledButton>
            </StyledFooter>
          )}
          {siteTheme === 'christmas' && (
            <>
              <StyledWinterBush
                src={winterBush}
                alt="Winter Bush"
                loading="lazy"
              />
              <StyledWinterBush
                src={winterBush}
                alt="Winter Bush"
                loading="lazy"
              />
            </>
          )}
        </StyledContainer>
      </StyledImageCards>
    )
  );
};

export default ImageCards;
